import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import placePledgeLogo from '../assets/place-pledge-logo.svg';

export const Page = styled.div`
  min-height: 100vh;
  padding: 2rem 1rem;
  background:
    radial-gradient(circle at 16% 0%, rgba(251, 191, 36, 0.25), transparent 27rem),
    radial-gradient(circle at 92% 8%, rgba(96, 165, 250, 0.38), transparent 28rem),
    linear-gradient(145deg, #101827 0%, #111827 48%, #1f2937 100%);
  color: #f8fafc;

  @media (max-width: 640px) {
    padding: 1.25rem 0.875rem 2rem;
  }
`;

export const Shell = styled.div`
  width: min(${({ $wide }) => ($wide ? '1180px' : '820px')}, 100%);
  margin: 0 auto;
`;

export const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

export const HeaderText = styled.div`
  width: 100%;
  min-width: 0;
`;

export const Eyebrow = styled.div`
  color: #93c5fd;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const BrandLogo = styled.img.attrs({
  src: placePledgeLogo,
  alt: 'Place Pledge'
})`
  display: block;
  width: min(260px, 72vw);
  height: auto;

  @media (max-width: 420px) {
    width: min(230px, 76vw);
  }
`;

export const BrandHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  min-height: 52px;
  margin-bottom: 1rem;
`;

export const HeaderIconButton = styled.button`
  position: relative;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(251, 191, 36, 0.34);
  border-radius: 999px;
  background: rgba(251, 191, 36, 0.14);
  color: #fbbf24;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: rgba(251, 191, 36, 0.22);
  }
`;

export const HomeIcon = styled.span`
  display: block;
  width: 22px;
  height: 22px;
  background: currentColor;
  mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 11.4 12 4l9 7.4v8.1a1.5 1.5 0 0 1-1.5 1.5h-5v-6.2h-5V21h-5A1.5 1.5 0 0 1 3 19.5v-8.1Z' fill='black'/%3E%3C/svg%3E") center / contain no-repeat;
  -webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 11.4 12 4l9 7.4v8.1a1.5 1.5 0 0 1-1.5 1.5h-5v-6.2h-5V21h-5A1.5 1.5 0 0 1 3 19.5v-8.1Z' fill='black'/%3E%3C/svg%3E") center / contain no-repeat;
`;

export const BackIcon = styled.span`
  display: block;
  width: 14px;
  height: 14px;
  border-left: 3px solid currentColor;
  border-bottom: 3px solid currentColor;
  transform: rotate(45deg) translateX(2px);
`;

export const PlusIcon = styled.span`
  position: relative;
  display: block;
  width: 18px;
  height: 18px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 7.5px;
    width: 18px;
    height: 3px;
    border-radius: 999px;
    background: currentColor;
  }

  &::after {
    transform: rotate(90deg);
  }
`;

export const Title = styled.h1`
  margin: 0.15rem 0 0;
  color: #f8fafc;
  font-size: clamp(2.5rem, 9vw, 5.25rem);
  line-height: 0.9;
  letter-spacing: -0.075em;
`;

export const Subtitle = styled.p`
  max-width: 42rem;
  margin: 0.85rem 0 0;
  color: rgba(226, 232, 240, 0.72);
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  font-weight: 700;
  line-height: 1.25;
`;

export const Card = styled.section`
  padding: clamp(1.1rem, 4vw, 1.75rem);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 32px;
  background: rgba(15, 23, 42, 0.72);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(18px);
`;

export const Panel = styled.div`
  padding: clamp(1rem, 3vw, 1.35rem);
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(251, 191, 36, 0.08)),
    rgba(255, 255, 255, 0.05);
`;

const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.7rem 1rem;
  border: 1px solid ${({ $danger }) => ($danger ? 'rgba(248, 113, 113, 0.32)' : 'rgba(255, 255, 255, 0.12)')};
  border-radius: 999px;
  background: ${({ $danger, $secondary }) => {
    if ($danger) return 'rgba(248, 113, 113, 0.18)';
    if ($secondary) return 'rgba(255, 255, 255, 0.1)';
    return 'linear-gradient(135deg, #fbbf24, #fb923c)';
  }};
  color: ${({ $danger, $secondary }) => ($danger || $secondary ? '#f8fafc' : '#111827')};
  font: inherit;
  font-weight: 900;
  text-decoration: none;
  cursor: pointer;
  box-shadow: ${({ $secondary, $danger }) => ($secondary || $danger ? 'none' : '0 18px 45px rgba(251, 146, 60, 0.22)')};

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const Button = styled.button`
  ${buttonStyles}
`;

export const ButtonLink = styled(Link)`
  ${buttonStyles}
`;

export const Field = styled.label`
  display: grid;
  gap: 0.45rem;
  color: #dbeafe;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const inputStyles = css`
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  padding: 0.75rem 0.95rem;
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.72);
  color: #f8fafc;
  font: inherit;
  font-size: 1rem;
  font-weight: 800;
  outline: none;

  &::placeholder {
    color: rgba(203, 213, 225, 0.45);
  }

  &:focus {
    border-color: rgba(251, 191, 36, 0.72);
    box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.12);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: #f8fafc;
    caret-color: #f8fafc;
    box-shadow: 0 0 0 1000px #111827 inset;
    transition: background-color 9999s ease-in-out 0s;
  }
`;

export const Input = styled.input`
  ${inputStyles}
`;

export const Select = styled.select`
  ${inputStyles}
`;

export const TextArea = styled.textarea`
  ${inputStyles}
  min-height: 120px;
  resize: vertical;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

export const Message = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 16px;
  background: ${({ $error }) => ($error ? 'rgba(127, 29, 29, 0.45)' : 'rgba(22, 101, 52, 0.45)')};
  color: ${({ $error }) => ($error ? '#fecaca' : '#bbf7d0')};
  font-weight: 800;
`;

export const Muted = styled.span`
  color: rgba(203, 213, 225, 0.6);
`;
